import React, { useState } from 'react';
import { Scatter } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import {
  ChartData,
  ScatterDataPoint,
  ChartDataset,
  ChartEvent,
  ActiveElement,
  Chart,
  ChartOptions,
} from 'chart.js';
import { useParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  Typography,
  Button,
  Dialog,
  IconButton,
  DialogContent,
  Grid,
  Paper,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';

import {
  useEsgStakeholdersAnswersAnalytics,
  useEsgSubTopic,
  useEsgTopic,
} from 'src/Esg/Dmav2/hooks';
import { StyledPanel } from 'src/Esg/styles';
import { StyledText, StyledTextLight } from '../styles';
import { StyledFlex } from 'src/components/StyledFlex';
import ChartAxesLegend from './ChartAxisLegend';
import { adjustDataPoints, getDataPoint, scatterOptions } from './utils';
import { useEsgReports } from 'src/Esg/hooks';
import {
  PositiveNegativeAvgDatapoint,
  EsgDmaTopicOutSchema,
  EsgDmaSubtopicOutSchema,
} from 'src/Esg/Dmav2/types';

interface ScatterChartProps {
  esg_stakeholder_id?: number;
}

interface LegendItemProps {
  label: string;
  color: string;
  keyLabel: string;
  onClick?: () => void;
}

// Reusable legend item component
const LegendItem: React.FC<LegendItemProps> = ({
  label,
  color,
  keyLabel,
  onClick,
}) => (
  <StyledFlex
    sx={{
      mb: '10px',
      justifyContent: 'flex-start',
      cursor: onClick ? 'pointer' : 'default',
    }}
    onClick={onClick}
  >
    <Avatar sx={{ bgcolor: color || '#D9D9D9' }}>
      <Typography variant="h6">{keyLabel}</Typography>
    </Avatar>
    <Typography variant="h6" sx={{ ml: '10px' }}>
      {label}
    </Typography>
  </StyledFlex>
);

// --- Moved ChartView Component ---
interface ChartViewProps {
  data: ChartData<'scatter', ScatterDataPoint[], string>;
  options: ChartOptions<'scatter'>;
  width?: string;
  height?: string;
}

const ChartView: React.FC<ChartViewProps> = ({
  data,
  options,
  width = '500px',
  height = '500px',
}) => (
  <Box sx={{ position: 'relative', width, height }}>
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
      }}
    >
      <ChartAxesLegend />
    </Box>
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        zIndex: 1,
      }}
    >
      <Scatter data={data} options={options} />
    </Box>
  </Box>
);

interface FullscreenViewProps {
  open: boolean;
  onClose: () => void;
  legendContent: React.ReactNode;
  chartData: ChartData<'scatter', ScatterDataPoint[], string>;
  chartOptions: ChartOptions<'scatter'>;
}

const FullscreenView: React.FC<FullscreenViewProps> = ({
  open,
  onClose,
  legendContent,
  chartData,
  chartOptions,
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'esg:dma.stakeholderAnalytics.dashboard',
  });
  const containerHeight = 'calc(100vh - 140px)';

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h5">{t('scatterPlot.title')}</Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 4 }}>
        <Grid container spacing={4} sx={{ height: containerHeight }}>
          <Grid item xs={12} md={8} lg={9} sx={{ height: '100%' }}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              {/* Use moved ChartView with passed props */}
              <ChartView
                width="100%"
                height="100%"
                data={chartData}
                options={chartOptions}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} md={4} lg={3} sx={{ height: '100%' }}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                height: '100%',
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Pass legend content directly */}
              {legendContent}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

// --- Main ScatterChart Component ---
export default function ScatterChart({
  esg_stakeholder_id,
}: ScatterChartProps) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'esg:dma.stakeholderAnalytics.dashboard',
  });

  // State to track selected topic
  const [selectedTopicId, setSelectedTopicId] = useState<number | null>(null);
  // State to track fullscreen mode
  const [fullscreen, setFullscreen] = useState(false);

  const { reportId } = useParams();
  const { _instance: report } = useEsgReports({ id: Number(reportId) });
  const { analytics } = useEsgStakeholdersAnswersAnalytics(
    report?.esg_dma_id,
    esg_stakeholder_id
  );
  const { topicsMap } = useEsgTopic();
  const { subTopicsMap } = useEsgSubTopic({ esg_dma_id: report?.esg_dma_id });

  // --- Legend Data Preparation ---
  type LegendView = 'topics' | 'subtopics' | 'fullscreen';
  type FullscreenLegendData = {
    topic: EsgDmaTopicOutSchema;
    subtopics: EsgDmaSubtopicOutSchema[];
  };
  type SubtopicsLegendData = {
    subtopic: EsgDmaSubtopicOutSchema;
    topic: EsgDmaTopicOutSchema;
  };
  type TopicsLegendData = EsgDmaTopicOutSchema;

  interface LegendInfo {
    view: LegendView;
    data: FullscreenLegendData[] | SubtopicsLegendData[] | TopicsLegendData[];
  }

  const getLegendData = (): LegendInfo => {
    if (fullscreen) {
      const groups: FullscreenLegendData[] = [];
      const topicGroupsMap: Record<number, EsgDmaSubtopicOutSchema[]> = {};

      analytics.summary.forEach((summary) => {
        const subtopic = subTopicsMap[summary.esg_dma_sub_topic_id];
        if (!subtopic) return;
        const topicId = subtopic.topic_id;
        if (!topicGroupsMap[topicId]) {
          topicGroupsMap[topicId] = [];
        }
        topicGroupsMap[topicId].push(subtopic);
      });

      Object.entries(topicGroupsMap).forEach(([topicIdStr, subtopicList]) => {
        const topicId = Number(topicIdStr);
        const topic = topicsMap[topicId];
        if (topic) {
          groups.push({
            topic,
            subtopics: [...subtopicList].sort((a, b) =>
              a.description.localeCompare(b.description)
            ),
          });
        }
      });
      groups.sort((a, b) => a.topic.key.localeCompare(b.topic.key));
      return { view: 'fullscreen', data: groups };
    } else if (selectedTopicId) {
      const items = analytics.summary
        .map((summary) => {
          const subtopic = subTopicsMap[summary.esg_dma_sub_topic_id];
          const topic = topicsMap?.[subtopic?.topic_id];
          if (!subtopic || !topic || topic.id !== selectedTopicId) return null;
          return { subtopic, topic };
        })
        .filter((item): item is SubtopicsLegendData => item !== null)
        .sort((a, b) =>
          a.subtopic.description.localeCompare(b.subtopic.description)
        );
      return { view: 'subtopics', data: items };
    } else {
      const items = analytics.topic_summary
        .map((surveySummary) => topicsMap[surveySummary.esg_dma_topic_id])
        .filter((topic): topic is TopicsLegendData => topic !== undefined)
        .sort((a, b) => a.key.localeCompare(b.key));
      return { view: 'topics', data: items };
    }
  };

  // Generic function to create datasets from summary data
  const createDatasets = <T extends PositiveNegativeAvgDatapoint>(
    summaryData: T[],
    getItemDetails: (item: T) => {
      label: string;
      description: string;
      color: string;
      key: string;
    } | null
  ): ChartDataset<'scatter', ScatterDataPoint[]>[] => {
    return adjustDataPoints(summaryData).map((adjustedItem, index) => {
      const details = getItemDetails(summaryData[index]);
      if (!details) return { label: '', data: [] };

      const item = summaryData[index];
      const dataPoint = getDataPoint(
        item,
        adjustedItem,
        details.key,
        details.description,
        details.color
      );

      // Add clip: false to the dataset configuration
      return {
        ...dataPoint,
        clip: false,
      };
    });
  };

  const getDatasetsByTopics = () =>
    createDatasets(analytics.topic_summary, (item) => {
      const topic = topicsMap[item.esg_dma_topic_id];
      if (!topic) return null;
      return {
        label: topic.name,
        description: topic.name,
        color: topic.color,
        key: topic.key,
      };
    });

  const getDatasetsBySubtopics = (
    topicId?: number
  ): ChartDataset<'scatter', ScatterDataPoint[]>[] => {
    const filteredSummary = topicId
      ? analytics.summary.filter(
          (item) =>
            subTopicsMap[item.esg_dma_sub_topic_id]?.topic_id === topicId
        )
      : analytics.summary;

    return createDatasets(filteredSummary, (item) => {
      const subtopic = subTopicsMap[item.esg_dma_sub_topic_id];
      const topic = topicsMap?.[subtopic?.topic_id];
      if (!subtopic || !topic) return null;
      return {
        label: subtopic.description,
        description: subtopic.description,
        color: topic.color,
        key: topic.key,
      };
    });
  };

  const handleTopicClick = (topicId: number) => {
    setSelectedTopicId(topicId);
  };

  const handleBackToTopics = () => {
    setSelectedTopicId(null);
  };

  const toggleFullscreen = () => {
    setFullscreen((prev) => !prev);
  };

  // Inline function to determine datasets based on state
  const getDatasets = (): ChartDataset<'scatter', ScatterDataPoint[]>[] => {
    if (fullscreen) return getDatasetsBySubtopics(); // All subtopics in fullscreen mode
    if (selectedTopicId) return getDatasetsBySubtopics(selectedTopicId);
    return getDatasetsByTopics();
  };

  // Get datasets based on selection state using the inline function
  const datasets = getDatasets();

  const chartData: ChartData<'scatter', ScatterDataPoint[], string> = {
    datasets,
  };

  // Event handler for chart click
  const chartOptions: ChartOptions<'scatter'> = {
    maintainAspectRatio: false,
    aspectRatio: undefined, // Set explicitly if maintainAspectRatio is false
    plugins: {
      legend: {
        display: false,
      },
      tooltip: scatterOptions.plugins?.tooltip,
    },
    scales: {
      x: {
        type: 'linear' as const,
        min: 0,
        max: 3.5,
        position: 'bottom',
        title: {
          display: true,
          text: t('barchart.negativeImpact'),
          padding: { top: 10 },
          font: {
            weight: 'bold',
            size: 14,
          },
        },
        ticks: {
          stepSize: 0.5,
          callback(value: number | string) {
            const numericValue =
              typeof value === 'string' ? parseFloat(value) : value;
            return numericValue > 3 ? '' : value;
          },
          padding: 10,
        },
        grid: {
          display: true,
          drawOnChartArea: true,
          lineWidth: 1,
          color: 'rgba(0, 0, 0, 0.1)',
        },
        border: {
          display: true,
        },
      },
      y: {
        type: 'linear' as const,
        min: 0,
        max: 3.5,
        position: 'left',
        title: {
          display: true,
          text: t('barchart.positiveImpact'),
          padding: { bottom: 10 },
          font: {
            weight: 'bold',
            size: 14,
          },
        },
        ticks: {
          stepSize: 0.5,
          callback(value: number | string) {
            const numericValue =
              typeof value === 'string' ? parseFloat(value) : value;
            return numericValue > 3 ? '' : value;
          },
          padding: 10,
        },
        grid: {
          display: true,
          drawOnChartArea: true,
          lineWidth: 1,
          color: 'rgba(0, 0, 0, 0.1)',
        },
        border: {
          display: true,
        },
      },
    },
    onClick: (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
      if (elements.length > 0 && !selectedTopicId && !fullscreen) {
        const { datasetIndex } = elements[0];
        const topicId = analytics.topic_summary[datasetIndex]?.esg_dma_topic_id;
        if (topicId) handleTopicClick(topicId);
      }
    },
  };

  // --- Rendering Logic ---
  const renderLegendItems = () => {
    const legendInfo = getLegendData();

    switch (legendInfo.view) {
      case 'fullscreen':
        return (legendInfo.data as FullscreenLegendData[]).map(
          ({ topic, subtopics }) => (
            <Box key={topic.id} sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                {topic.name}
              </Typography>
              {subtopics.map((subtopic) => (
                <LegendItem
                  key={subtopic.id}
                  label={subtopic.description}
                  color={topic.color || '#D9D9D9'}
                  keyLabel={topic.key}
                />
              ))}
            </Box>
          )
        );

      case 'subtopics':
        return (legendInfo.data as SubtopicsLegendData[]).map(
          ({ subtopic, topic }) => (
            <LegendItem
              key={subtopic.id}
              label={subtopic.description}
              color={topic.color || '#D9D9D9'}
              keyLabel={topic.key}
            />
          )
        );

      case 'topics':
      default:
        return (legendInfo.data as TopicsLegendData[]).map((topic) => (
          <LegendItem
            key={topic.id}
            label={topic.name}
            color={topic.color || '#D9D9D9'}
            keyLabel={topic.key}
            onClick={() => handleTopicClick(topic.id)}
          />
        ));
    }
  };

  // Regular view with same height containers
  return (
    <>
      <StyledPanel sx={{ minWidth: '860px' }}>
        <StyledFlex
          sx={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          <div>
            <StyledText>{t('scatterPlot.title')}</StyledText>
            <StyledTextLight>{t('scatterPlot.subtitle')}</StyledTextLight>
          </div>
          <StyledFlex>
            {selectedTopicId && (
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleBackToTopics}
                variant="outlined"
                size="small"
                sx={{ mr: 1 }}
              >
                {t('buttons.backToTopics')}
              </Button>
            )}
            <Button
              startIcon={<FullscreenIcon />}
              onClick={toggleFullscreen}
              variant="outlined"
              size="small"
            >
              {t('buttons.fullscreen')}
            </Button>
          </StyledFlex>
        </StyledFlex>
        <StyledFlex
          sx={{
            justifyContent: 'flex-start',
            alignItems: 'stretch',
            mt: '26px',
            gap: '46px',
            height: '500px',
          }}
        >
          <Box sx={{ flex: '1 1 500px', maxWidth: '500px' }}>
            <ChartView
              width="100%"
              height="100%"
              data={chartData}
              options={chartOptions}
            />
          </Box>

          <Box
            sx={{
              flex: '0 0 auto',
              overflow: 'auto',
              maxHeight: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {renderLegendItems()}
          </Box>
        </StyledFlex>
      </StyledPanel>
      {/* Use moved FullscreenView */}
      <FullscreenView
        open={fullscreen}
        onClose={toggleFullscreen}
        legendContent={renderLegendItems()}
        chartData={chartData}
        chartOptions={chartOptions}
      />
    </>
  );
}
